/**
 * Browser half of Firebase phone sign-in (D30), after procurementpro's
 * lib/firebase-phone.ts. Firebase sends the SMS (invisible reCAPTCHA +
 * signInWithPhoneNumber); the confirmed code yields an ID token that the
 * server checks (Customer.verifyFirebase). The store session is ours, so the
 * Firebase session is signed out straight away.
 *
 * The SDK is imported on demand: stores using our own codes never load it.
 */

async function authFor(config) {
  const [{ getApp, getApps, initializeApp }, authMod] = await Promise.all([import('firebase/app'), import('firebase/auth')]);
  const name = `mp-${config.projectId}`;
  const app = getApps().some((a) => a.name === name)
    ? getApp(name)
    : initializeApp({ apiKey: config.apiKey, projectId: config.projectId, authDomain: config.authDomain, appId: config.appId }, name);
  return { auth: authMod.getAuth(app), authMod };
}

/**
 * Send a code to `phoneE164`. `containerId` names an empty element for the
 * invisible reCAPTCHA. Resolves to { confirm(code) → ID token }.
 */
export async function sendFirebaseCode(config, phoneE164, containerId) {
  const { auth, authMod } = await authFor(config);
  const container = document.getElementById(containerId);
  if (container) container.innerHTML = '';
  const verifier = new authMod.RecaptchaVerifier(auth, containerId, { size: 'invisible' });
  try {
    const result = await authMod.signInWithPhoneNumber(auth, phoneE164, verifier);
    return {
      async confirm(code) {
        try {
          const cred = await result.confirm(String(code).trim());
          const idToken = await cred.user.getIdToken();
          await authMod.signOut(auth).catch(() => undefined);
          return idToken;
        } catch (e) {
          throw new Error(firebaseErrorMessage(e));
        }
      },
    };
  } catch (e) {
    throw new Error(firebaseErrorMessage(e));
  } finally {
    verifier.clear();
  }
}

const MESSAGES = {
  'auth/invalid-phone-number': 'Enter a valid 10-digit Indian mobile number.',
  'auth/missing-phone-number': 'Enter your mobile number.',
  'auth/too-many-requests': 'Too many attempts from this device. Please wait a while and try again.',
  'auth/quota-exceeded': 'We cannot send codes right now. Please try again later.',
  'auth/unauthorized-domain': 'Sign-in by SMS is not set up for this store address yet. Please contact the store.',
  'auth/operation-not-allowed': 'Sign-in by SMS is not available right now. Please try again later.',
  'auth/billing-not-enabled': 'Sign-in by SMS is not available right now. Please try again later.',
  'auth/invalid-app-credential': 'The security check failed. Reload the page and try again.',
  'auth/captcha-check-failed': 'The security check failed. Reload the page and try again.',
  'auth/invalid-verification-code': 'That code is not right. Check the SMS and try again.',
  'auth/code-expired': 'This code has expired. Request a new one.',
  'auth/network-request-failed': 'No connection. Check your internet and try again.',
};

export function firebaseErrorMessage(e) {
  return MESSAGES[(e && e.code) || ''] || 'Phone verification failed. Please try again.';
}
